import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import {
    PostProjectTaskPhase,
    PatchProjectTaskPhase,
    DeleteProjectTaskPhase,
    PatchTask,
} from '../../redux/taskSlice';
import { Add16Regular, Dismiss16Regular, Edit16Regular, Delete16Regular } from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList } from '@fluentui/react-components';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import SimpleAlertModal from '../CommonComponents/DoubleConfirmationModal/SimpleAlertModal';
import styles from './css/index.module.css';

const ProjectPhaseManagementMenu = ({ onPhaseSelect, children, taskData, selectedPhaseName }) => {
    const dispatch = useDispatch();
    const { projectPhases, taskData: allProjectTasks } = useSelector(state => state.task);
    const [newPhaseName, setNewPhaseName] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [open, setOpen] = useState(false);
    const selectedItemRef = useRef(null);
    const [editingPhaseId, setEditingPhaseId] = useState(null);
    const [editingPhaseName, setEditingPhaseName] = useState('');
    const [phaseActionLoadingId, setPhaseActionLoadingId] = useState(null);
    const [phasePendingDelete, setPhasePendingDelete] = useState(null);
    const [cannotDeletePhaseName, setCannotDeletePhaseName] = useState('');
    const deleteModalRef = useRef(null);
    const [manageModalOpen, setManageModalOpen] = useState(false);

    useEffect(() => {
        if (!open || !selectedPhaseName) return;
        const timer = setTimeout(() => {
            selectedItemRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 0);
        return () => clearTimeout(timer);
    }, [open, selectedPhaseName]);

    const getAllPhases = () => {
        const phaseMap = new Map();

        if (projectPhases && projectPhases.length > 0) {
            projectPhases.forEach(phase => {
                phaseMap.set(phase.name, { ...phase, source: 'project' });
            });
        }

        let tasks = [];
        if (Array.isArray(taskData)) {
            tasks = taskData;
        } else if (taskData && taskData.data && Array.isArray(taskData.data)) {
            tasks = taskData.data;
        } else if (taskData && taskData.groups && Array.isArray(taskData.groups)) {
            tasks = taskData.groups.flatMap(group => group.tasks || []);
        }

        tasks.forEach(task => {
            const phaseName = task?.project_phase;
            if (phaseName && phaseName.trim() !== '' && !phaseMap.has(phaseName)) {
                phaseMap.set(phaseName, {
                    id: `task_${phaseName}`,
                    name: phaseName,
                    source: 'task',
                });
            }
        });

        return Array.from(phaseMap.values());
    };

    const allPhases = getAllPhases();

    const isPhaseInUse = phase => {
        if (!phase || !phase.name) return false;
        const normalizedTarget = phase.name.toString().trim().toLowerCase();
        if (!normalizedTarget) return false;

        const sourceTasks = Array.isArray(allProjectTasks) ? allProjectTasks : [];
        return sourceTasks.some(task => {
            const taskPhase = (task.project_phase || '').toString().trim().toLowerCase();
            return taskPhase === normalizedTarget;
        });
    };

    const managedPhases = (() => {
        const list = [];
        const projectPhaseByName = new Map();

        (projectPhases || []).forEach(phase => {
            if (!phase?.name) return;
            projectPhaseByName.set(phase.name, phase);
        });

        const inferredOnlyNames = new Set();
        allPhases.forEach(phase => {
            if (!phase?.name) return;
            if (phase.source === 'task' && !projectPhaseByName.has(phase.name)) {
                inferredOnlyNames.add(phase.name);
            }
        });

        projectPhaseByName.forEach(phase => {
            list.push({
                id: phase.id,
                name: phase.name,
                isProjectPhase: true,
            });
        });

        inferredOnlyNames.forEach(name => {
            list.push({
                id: `task_only_${name}`,
                name,
                isProjectPhase: false,
            });
        });

        return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    })();

    const handleAddPhase = async () => {
        if (!newPhaseName.trim()) return;

        setIsAdding(true);
        try {
            await dispatch(PostProjectTaskPhase({ name: newPhaseName.trim() }));
            setNewPhaseName('');
        } catch (error) {
            console.error('Error adding project phase:', error);
        } finally {
            setIsAdding(false);
        }
    };

    const handlePhaseClick = phase => {
        onPhaseSelect(phase);
        setOpen(false);
    };

    const handleStartEditPhase = phase => {
        if (!phase) return;
        setEditingPhaseId(phase.id);
        setEditingPhaseName(phase.name || '');
    };

    const handleCancelEditPhase = () => {
        setEditingPhaseId(null);
        setEditingPhaseName('');
    };

    const handleSaveEditPhase = async phase => {
        if (!phase) return;
        const trimmed = editingPhaseName.trim();
        if (!trimmed || trimmed === phase.name) {
            handleCancelEditPhase();
            return;
        }

        const oldName = (phase.name || '').toString();
        const newName = trimmed;

        try {
            setPhaseActionLoadingId(phase.id);

            if (phase.isProjectPhase) {
                await dispatch(
                    PatchProjectTaskPhase({
                        id: phase.id,
                        body: { name: newName },
                    }),
                );
            }

            const sourceTasks = Array.isArray(allProjectTasks) ? allProjectTasks : [];
            const normalizedOld = oldName.toString().trim().toLowerCase();
            const patchPromises = [];

            sourceTasks.forEach(task => {
                const taskPhase = (task.project_phase || '').toString().trim().toLowerCase();
                if (taskPhase !== normalizedOld) return;

                patchPromises.push(
                    dispatch(
                        PatchTask({
                            body: { project_phase: newName },
                            id: task.id,
                            suppressToast: true,
                        }),
                    ),
                );
            });

            if (patchPromises.length > 0) {
                await Promise.all(patchPromises);
            }

            handleCancelEditPhase();
        } catch (error) {
            console.error('Error updating project phase:', error);
        } finally {
            setPhaseActionLoadingId(null);
        }
    };

    const handleDeletePhase = async phase => {
        if (!phase) return;

        if (isPhaseInUse(phase)) {
            setCannotDeletePhaseName(phase.name || '');
            return;
        }

        if (!phase.isProjectPhase) {
            return;
        }

        try {
            setPhaseActionLoadingId(phase.id);
            await dispatch(DeleteProjectTaskPhase(phase.id));
            if (editingPhaseId === phase.id) {
                handleCancelEditPhase();
            }
        } catch (error) {
            console.error('Error deleting project phase:', error);
        } finally {
            setPhaseActionLoadingId(null);
        }
    };

    return (
        <>
            {cannotDeletePhaseName && (
                <SimpleAlertModal
                    open={!!cannotDeletePhaseName}
                    onClose={() => setCannotDeletePhaseName('')}
                    message="This project phase cannot be deleted because it is currently associated with one or more tasks."
                />
            )}
            <DoubleConfirmationModal
                isMenuChild={true}
                message="Deleting a Project Phase permanently removes it from the Project, and this is irreversible."
                handleAction={() => {
                    if (phasePendingDelete) {
                        handleDeletePhase(phasePendingDelete);
                    }
                }}>
                <span ref={deleteModalRef} />
            </DoubleConfirmationModal>

            <Menu open={open} onOpenChange={(e, data) => setOpen(data.open)}>
                <MenuTrigger>{children}</MenuTrigger>
                <MenuPopover className={styles.entitySelectorPopover}>
                    <MenuList className={styles.entitySelectorList}>
                        <div className={styles.entitySelectorItems}>
                            {allPhases.length > 0 ? (
                                allPhases.map(phase => {
                                    const isSelected = !!selectedPhaseName && phase.name === selectedPhaseName;
                                    return (
                                        <div
                                            key={phase.id}
                                            ref={isSelected ? selectedItemRef : undefined}
                                            onClick={() => handlePhaseClick(phase)}
                                            className={`${styles.entitySelectorItem} ${isSelected ? styles.entitySelectorItemSelected : ''}`}>
                                            <span className={styles.entitySelectorItemLabel}>{phase.name}</span>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className={styles.entitySelectorEmpty}>No project phases available</div>
                            )}
                        </div>

                        {allPhases.length > 0 ? (
                            <>
                                <div className={styles.entitySelectorDivider} />
                                <div
                                    className={styles.entitySelectorItem}
                                    onClick={() => {
                                        setManageModalOpen(true);
                                        setOpen(false);
                                        handleCancelEditPhase();
                                    }}>
                                    <span className={styles.entitySelectorItemLabel}>Edit Project Phases</span>
                                </div>
                            </>
                        ) : null}

                        {selectedPhaseName ? (
                            <>
                                <div className={styles.entitySelectorDivider} />
                                <div
                                    className={styles.entitySelectorRemove}
                                    onClick={() => {
                                        onPhaseSelect(null);
                                        setOpen(false);
                                    }}>
                                    <span className={styles.entitySelectorRemoveLabel}>Remove Project Phase</span>
                                    <Delete16Regular className={styles.entitySelectorRemoveIcon} />
                                </div>
                            </>
                        ) : null}

                        <div className={styles.entitySelectorDivider} />
                        <div className={styles.entitySelectorAddRow}>
                            <input
                                type="text"
                                placeholder="Add new project phase"
                                className={styles.entitySelectorInput}
                                value={newPhaseName}
                                onChange={e => setNewPhaseName(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddPhase();
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className={styles.entitySelectorAddBtn}
                                onClick={handleAddPhase}
                                disabled={!newPhaseName.trim() || isAdding}
                                aria-label="Add project phase">
                                <Add16Regular />
                            </button>
                        </div>
                    </MenuList>
                </MenuPopover>
            </Menu>

            <Modal
                centered
                show={manageModalOpen}
                onHide={() => {
                    setManageModalOpen(false);
                    handleCancelEditPhase();
                }}
                className={styles.modalMainBody}>
                <Modal.Body className={styles.confirmationModalBody}>
                    <div className={styles.confirmationModalMsg} style={{ width: '100%' }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className={styles.tableFilterHeader + ' mb-0'}>Project Phases</h6>
                            <button
                                type="button"
                                onClick={() => {
                                    setManageModalOpen(false);
                                    handleCancelEditPhase();
                                }}
                                className={styles.closeButton || 'btn btn-link p-0'}
                                style={{ border: 'none', background: 'transparent' }}>
                                <Dismiss16Regular color="var(--Brand-Primary)" />
                            </button>
                        </div>

                        {managedPhases && managedPhases.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {managedPhases.map(phase => {
                                    const isEditingThis = editingPhaseId === phase.id;
                                    return (
                                        <div
                                            key={phase.id}
                                            className={styles.tagMenuItem}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 8,
                                            }}>
                                            {isEditingThis ? (
                                                <input
                                                    type="text"
                                                    className={styles.tagInput}
                                                    style={{ flex: 1 }}
                                                    value={editingPhaseName}
                                                    onChange={e => setEditingPhaseName(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleSaveEditPhase(phase);
                                                        } else if (e.key === 'Escape') {
                                                            e.preventDefault();
                                                            handleCancelEditPhase();
                                                        }
                                                    }}
                                                    onBlur={() => handleSaveEditPhase(phase)}
                                                    autoFocus
                                                />
                                            ) : (
                                                <span style={{ flex: 1 }}>{phase.name}</span>
                                            )}
                                            {!isEditingThis && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <Edit16Regular
                                                        style={{
                                                            color: 'var(--Neutral-Gray-6)',
                                                            cursor: phaseActionLoadingId ? 'not-allowed' : 'pointer',
                                                            opacity: phaseActionLoadingId === phase.id ? 0.5 : 1,
                                                        }}
                                                        onClick={() => {
                                                            if (phaseActionLoadingId) return;
                                                            handleStartEditPhase(phase);
                                                        }}
                                                    />
                                                    <Delete16Regular
                                                        style={{
                                                            color: 'var(--Neutral-Gray-6)',
                                                            cursor: phaseActionLoadingId ? 'not-allowed' : 'pointer',
                                                            opacity: phaseActionLoadingId === phase.id ? 0.5 : 1,
                                                        }}
                                                        onClick={() => {
                                                            if (phaseActionLoadingId) return;
                                                            if (isPhaseInUse(phase)) {
                                                                setCannotDeletePhaseName(phase.name || '');
                                                                return;
                                                            }
                                                            setPhasePendingDelete(phase);
                                                            deleteModalRef.current && deleteModalRef.current.click();
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="mb-0">No project phases available.</p>
                        )}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ProjectPhaseManagementMenu;
